import { type ImageSource, type ImageVariant, ImageVariantListSchema } from "../schema/image";

/**
 * PRTL-1349: image optimisation rules shared by the server pipeline and both
 * clients. Pure — no I/O — so they are unit-tested directly.
 */

/** Widths generated for every processed image. Never upscaled. */
export const IMAGE_VARIANT_WIDTHS = [320, 800, 1600] as const;

/** Originals above this are left alone (served as uploaded, no variants). */
export const IMAGE_PROCESSING_MAX_BYTES = 25 * 1024 * 1024;

/** Refuse to decode anything larger than this many pixels (decompression bombs). */
export const IMAGE_PROCESSING_MAX_PIXELS = 50_000_000;

/**
 * Raster formats the pipeline converts. GIF is excluded (animation would be
 * lost), SVG is vector already, HEIC needs codecs we don't ship.
 */
export const PROCESSABLE_IMAGE_MIME_TYPES = [
    "image/jpeg",
    "image/png",
    "image/webp",
    "image/avif",
    "image/tiff",
] as const;

export function isProcessableImageMime(mime: string | null | undefined): boolean {
    return !!mime && (PROCESSABLE_IMAGE_MIME_TYPES as readonly string[]).includes(mime);
}

/**
 * Which variant widths to generate for an original of `width` px: every
 * configured width strictly smaller than the original. An original narrower
 * than the smallest width gets none — it is already small.
 */
export function planVariantWidths(width: number): number[] {
    if (!Number.isFinite(width) || width <= 0) return [];
    return IMAGE_VARIANT_WIDTHS.filter((w) => w < width);
}

/** Height of a variant scaled to `targetWidth`, preserving aspect ratio. */
export function scaledHeight(width: number, height: number, targetWidth: number): number {
    return Math.max(1, Math.round((height * targetWidth) / width));
}

/** The S3 key of a variant: next to the original, format in the extension. */
export function variantKey(originalKey: string, width: number): string {
    return `${originalKey}_w${width}.webp`;
}

/** Parse a JSON column into variants; anything malformed reads as none. */
export function parseImageVariants(value: unknown): ImageVariant[] {
    const parsed = ImageVariantListSchema.safeParse(value);
    return parsed.success ? parsed.data : [];
}

/** `srcset` string: variants ascending, plus the original at its true width. */
export function buildSrcSet(
    originalUrl: string,
    originalWidth: number | null | undefined,
    variants: ImageVariant[],
): string | undefined {
    if (variants.length === 0) return undefined;
    const parts = [...variants]
        .sort((a, b) => a.width - b.width)
        .map((v) => `${v.url} ${v.width}w`);
    if (originalWidth && originalWidth > (variants.at(-1)?.width ?? 0)) {
        parts.push(`${originalUrl} ${originalWidth}w`);
    }
    return parts.join(", ");
}

/** Build the DTO for an image from its stored columns. */
export function buildImageSource(
    url: string,
    width: number | null | undefined,
    height: number | null | undefined,
    variantsJson: unknown,
): ImageSource {
    const variants = parseImageVariants(variantsJson);
    const srcSet = buildSrcSet(url, width, variants);
    return {
        src: url,
        ...(srcSet ? { srcSet } : {}),
        ...(width ? { width } : {}),
        ...(height ? { height } : {}),
        ...(variants.length ? { variants } : {}),
    };
}

/**
 * For clients that can't use `srcset` (navigator's on-device image cache keys
 * on one URL): the smallest variant at least `neededPx` wide, else the
 * original. `neededPx` is CSS width × device pixel ratio.
 */
export function pickImageUrl(image: ImageSource, neededPx: number): string {
    const sorted = [...(image.variants ?? [])].sort((a, b) => a.width - b.width);
    const fit = sorted.find((v) => v.width >= neededPx);
    if (fit) return fit.url;
    // Nothing big enough: the original is the largest there is.
    return image.src;
}

/**
 * Client-side downscale plan for an upload (web): null when the file should
 * go up untouched, else the target size. Only large raster images are touched.
 */
export const CLIENT_DOWNSCALE_MIN_BYTES = 2 * 1024 * 1024;
export const CLIENT_DOWNSCALE_MAX_EDGE = 2560;

export function planClientDownscale(input: {
    type: string;
    sizeBytes: number;
    width: number;
    height: number;
}): { width: number; height: number } | null {
    if (!["image/jpeg", "image/png", "image/webp"].includes(input.type)) return null;
    if (input.sizeBytes < CLIENT_DOWNSCALE_MIN_BYTES) return null;
    const longEdge = Math.max(input.width, input.height);
    if (longEdge <= CLIENT_DOWNSCALE_MAX_EDGE) return null;
    const scale = CLIENT_DOWNSCALE_MAX_EDGE / longEdge;
    return {
        width: Math.max(1, Math.round(input.width * scale)),
        height: Math.max(1, Math.round(input.height * scale)),
    };
}
